import { App as AntdApp, Button, Descriptions, Drawer, Space, Spin, Tag, Typography } from 'antd'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import StatusTag from '@/components/feedback/StatusTag'
import { useGenerateDraft } from '@/modules/draft/queries'
import { useMessageDetail, useQueueMessageMediaDownload } from '@/modules/message/queries'
import { formatMessageDateTime } from '@/utils/datetime'
import { canGenerateDraftFromMessage } from '../messageDraftRules'
import WechatConversation from './WechatConversation'

const { Text } = Typography

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : '操作失败'
}

interface Props {
  messageId: string | null
  onClose: () => void
}

export default function MessageDetailDrawer({ messageId, onClose }: Props) {
  const { message } = AntdApp.useApp()
  const navigate = useNavigate()
  const { data, isLoading, refetch } = useMessageDetail(messageId)
  const generateDraft = useGenerateDraft()
  const queueMediaDownload = useQueueMessageMediaDownload()
  const canGenerateDraft = data
    ? canGenerateDraftFromMessage(data.message_status, data.operator_actions)
    : false
  const hasActiveMediaDownload =
    data?.media.some((item) => ['pending', 'downloading'].includes(item.download_status)) ?? false

  useEffect(() => {
    if (!messageId || !hasActiveMediaDownload) return
    const timer = window.setInterval(() => {
      void refetch()
    }, 3000)
    return () => window.clearInterval(timer)
  }, [hasActiveMediaDownload, messageId, refetch])

  return (
    <Drawer
      title="消息详情"
      size="large"
      open={!!messageId}
      onClose={onClose}
      destroyOnHidden
      extra={
        canGenerateDraft ? (
          <Button
            type="primary"
            size="small"
            loading={generateDraft.isPending}
            onClick={async () => {
              if (!data?.message_id) return
              const messageKey = `generate-draft-${data.message_id}`
              message.loading({ content: '正在生成草稿，请稍候...', key: messageKey, duration: 0 })
              try {
                const result = await generateDraft.mutateAsync(data.message_id)
                if (!result.id) throw new Error('后端未返回草稿ID')
                message.success({ content: '草稿生成成功，正在打开...', key: messageKey })
                onClose()
                navigate(`/drafts/${result.id}`)
              } catch (error) {
                message.error({ content: `草稿生成失败：${errorMessage(error)}`, key: messageKey, duration: 5 })
              }
            }}
          >
            生成草稿
          </Button>
        ) : null
      }
    >
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin />
        </div>
      ) : data ? (
        <Space orientation="vertical" style={{ width: '100%' }} size="middle">
          <Descriptions size="small" column={2} bordered>
            <Descriptions.Item label="消息ID">{data.message_id}</Descriptions.Item>
            <Descriptions.Item label="状态">
              <StatusTag type="message" status={data.message_status} />
            </Descriptions.Item>
            <Descriptions.Item label="群名">{data.room_name}</Descriptions.Item>
            <Descriptions.Item label="客户">{data.customer_name || '-'}</Descriptions.Item>
            <Descriptions.Item label="发送人">{data.sender_name}</Descriptions.Item>
            <Descriptions.Item label="发送时间">{formatMessageDateTime(data.send_time)}</Descriptions.Item>
            <Descriptions.Item label="入库时间">{formatMessageDateTime(data.ingested_at)}</Descriptions.Item>
          </Descriptions>

          <WechatConversation
            messages={[
              {
                message_id: data.message_id,
                sender_name: data.sender_name,
                send_time: data.send_time,
                content_preview: data.raw_content || data.normalized_content,
                media: data.media,
              },
            ]}
            pendingMediaUid={
              queueMediaDownload.isPending ? queueMediaDownload.variables?.mediaUid : undefined
            }
            onQueueDownload={(messageId, mediaUid) => {
              queueMediaDownload.mutate(
                { messageId, mediaUid },
                { onSuccess: () => message.success('已加入下载队列') },
              )
            }}
          />

          {data.attachments.length > 0 && (
            <div>
              <Text strong>附件摘要</Text>
              <div style={{ marginTop: 8 }}>
                {data.attachments.map((att, i) => (
                  <Tag key={i}>{att.name}</Tag>
                ))}
              </div>
            </div>
          )}

          {data.linked_drafts.length > 0 && (
            <div>
              <Text strong>关联草稿</Text>
              <div style={{ marginTop: 8 }}>
                {data.linked_drafts.map((draft) => (
                  <Tag key={draft.id} color="blue">{draft.draft_no} ({draft.status})</Tag>
                ))}
              </div>
            </div>
          )}

          {data.message_events.length > 0 && (
            <div>
              <Text strong>事件时间线</Text>
              <div style={{ marginTop: 8, borderLeft: '2px solid #d9d9d9', paddingLeft: 12 }}>
                {data.message_events.map((evt, i) => (
                  <div key={i} style={{ marginBottom: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>{formatMessageDateTime(evt.created_at)}</Text>
                    <br />
                    <Text>{evt.note}</Text>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Space>
      ) : null}
    </Drawer>
  )
}
